import { useCallback, useEffect, useRef, useState } from 'react';
import { attractAudio, audioAsset } from '../lib/attractAudio';

export function ArcadeMarquee({ credits, fastMode, onFastChange, onFinaleShortcut }: { credits: number; fastMode: boolean; onFastChange: (active: boolean) => void; onFinaleShortcut: () => void }) {
  const activePointer = useRef<number | null>(null);
  const activated = useRef(false);
  const suppressClick = useRef(false);
  const heckleTimer = useRef<number | null>(null);
  const shortcutArmed = useRef(false);
  const shortcutClicks = useRef(0);
  const shortcutComplete = useRef(false);
  const [heckling, setHeckling] = useState(false);

  const endHold = useCallback(() => {
    if (!activated.current) return;
    activated.current = false;
    suppressClick.current = true;
    onFastChange(false);
  }, [onFastChange]);

  const beginHold = useCallback(() => {
    if (activated.current) return;
    activated.current = true;
    onFastChange(true);
  }, [onFastChange]);

  useEffect(() => {
    const endPointerHold = (event: PointerEvent) => {
      if (activePointer.current !== event.pointerId) return;
      activePointer.current = null;
      endHold();
    };
    const endInterruptedHold = () => {
      activePointer.current = null;
      endHold();
    };
    const endOnHide = () => { if (document.hidden) endInterruptedHold(); };
    window.addEventListener('pointerup', endPointerHold);
    window.addEventListener('pointercancel', endPointerHold);
    window.addEventListener('blur', endInterruptedHold);
    document.addEventListener('visibilitychange', endOnHide);
    return () => {
      window.removeEventListener('pointerup', endPointerHold);
      window.removeEventListener('pointercancel', endPointerHold);
      window.removeEventListener('blur', endInterruptedHold);
      document.removeEventListener('visibilitychange', endOnHide);
      if (heckleTimer.current !== null) window.clearTimeout(heckleTimer.current);
    };
  }, [endHold]);

  const triggerHeckle = () => {
    setHeckling(true);
    if (heckleTimer.current !== null) window.clearTimeout(heckleTimer.current);
    void attractAudio.play(audioAsset('/assets/audio/easter-eggs/heckler-haw.wav'), { kind: 'voice', gain: 0.95 });
    heckleTimer.current = window.setTimeout(() => { setHeckling(false); heckleTimer.current = null; }, 2800);
  };

  const playShortcutConfirm = (playbackRate = 1) => {
    void attractAudio.play(audioAsset('/assets/audio/finale-game/weapon-equip.wav'), { kind: 'effect', gain: 0.96, playbackRate });
  };

  const armFinaleShortcut = () => {
    if (shortcutComplete.current) return;
    shortcutArmed.current = true;
    shortcutClicks.current = 0;
    playShortcutConfirm(0.84);
  };

  const advanceFinaleShortcut = () => {
    if (!shortcutArmed.current || shortcutComplete.current) return;
    shortcutClicks.current += 1;
    if (shortcutClicks.current < 20) return;
    shortcutArmed.current = false;
    shortcutComplete.current = true;
    playShortcutConfirm(1.18);
    void attractAudio.play(audioAsset('/assets/audio/ui/finale-sting.wav'), { kind: 'effect', gain: 0.9, delaySeconds: 0.08 });
    onFinaleShortcut();
  };

  const renderPrompt = (side: 'left' | 'right') => credits > 0
    ? <b className="arcade-marquee__credits">CREDITS {credits}/2</b>
    : fastMode
      ? <b>TURBO ATTRACT // 4X</b>
    : <>
      <button className="arcade-marquee__coin" type="button" aria-label={`${side === 'left' ? 'Left' : 'Right'} insert coin`} onClick={side === 'left' ? armFinaleShortcut : advanceFinaleShortcut}>INSERT COIN</button>
      <button className="arcade-marquee__heckle-button" type="button" onClick={triggerHeckle}>{heckling ? 'HAW HAW' : 'CLICK HERE'}</button>
    </>;

  return (
    <header className="arcade-marquee" aria-label="The Smackers arcade marquee">
      <span className="arcade-marquee__prompt">{renderPrompt('left')}</span>
      <button
        className="arcade-marquee__brand"
        type="button"
        aria-label="The Smackers: Island Beatdown"
        aria-pressed={fastMode}
        onPointerDown={(event) => {
          if (event.button !== 0) return;
          activePointer.current = event.pointerId;
          event.currentTarget.setPointerCapture(event.pointerId);
          beginHold();
        }}
        onPointerUp={(event) => {
          if (activePointer.current !== event.pointerId) return;
          activePointer.current = null;
          endHold();
        }}
        onPointerCancel={(event) => {
          if (activePointer.current !== event.pointerId) return;
          activePointer.current = null;
          endHold();
        }}
        onKeyDown={(event) => { if (!event.repeat && (event.key === ' ' || event.key === 'Enter')) beginHold(); }}
        onKeyUp={(event) => { if (event.key === ' ' || event.key === 'Enter') endHold(); }}
        onClick={(event) => {
          if (!suppressClick.current) return;
          event.preventDefault();
          suppressClick.current = false;
        }}
      >
        <img src="/assets/references/logo_game.png" alt="The Smackers" width="981" height="369" />
        <p>Island Beatdown // Fighting Tournament</p>
      </button>
      <span className="arcade-marquee__prompt arcade-marquee__prompt--right">{renderPrompt('right')}</span>
      {heckling && <aside className="arcade-heckler" aria-live="assertive"><img src="/assets/attract/shared/heckler.gif" alt="Nelson points and laughs" /><strong>HAW HAW!</strong></aside>}
    </header>
  );
}
